import { components } from "@/lib/ApiSchema";
import { RFQListItem } from "@/lib/queries/types";
import { rfqStatusLabel } from "@/lib/utils";
import { HTMLAttributes, ReactNode, useId } from "react";
import { Tooltip } from "react-tooltip";
import AlertIcon from "~icons/local/alert";
import NoBidIcon from "~icons/local/no-bid";

export default function RFQStatusTableCell({ rfq }: { rfq: RFQListItem }) {
  const tooltipId = useId();

  if (rfq.has_processing_failures) {
    return (
      <>
        <SpecialStatus
          label="Processing..."
          className="text-slate-500 bg-slate-100"
          icon={<AlertIcon />}
          data-tooltip-id={tooltipId}
        />
        <Tooltip
          id={tooltipId}
          content="Uh-oh! We ran into some issues processing this RFQ. Our team has been alerted and is looking into the problem."
        />
      </>
    );
  }

  switch (rfq.status) {
    case "new":
    case "estimated":
    case "quote_created":
    case "sent_quote":
      return (
        <div className="flex gap-2 items-center">
          <Progress status={rfq.status} />
          <div className="text-xs text-nowrap">
            {rfqStatusLabel[rfq.status]}
          </div>
        </div>
      );
    case "sent_no_bid":
      return (
        <SpecialStatus
          label="No Bid"
          icon={<NoBidIcon />}
          className="border-red-900 text-red-900 bg-red-50"
        />
      );
    case "external_response":
      return (
        <SpecialStatus
          label="External Response"
          className="border-slate-500 text-slate-500 bg-slate-100"
        />
      );
    case "processing":
      return (
        <SpecialStatus
          label="Processing…"
          className="text-slate-500 bg-slate-100"
        />
      );
    case "archived":
      return (
        <SpecialStatus
          label="Archived"
          className="border-slate-500 text-slate-500 bg-slate-100"
        />
      );
    default:
      return null;
  }
}

function Progress({ status }: { status: components["schemas"]["RFQStatus"] }) {
  return (
    <div className="flex gap-1">
      <div
        className={`h-4 w-9 rounded-tl-full rounded-bl-full ${
          ["estimated", "quote_created", "sent_quote"].includes(status)
            ? "bg-blue-600"
            : "bg-slate-200"
        }`}
      />
      <div
        className={`h-4 w-9 ${
          ["quote_created", "sent_quote"].includes(status)
            ? "bg-blue-600"
            : "bg-slate-200"
        }`}
      />
      <div
        className={`h-4 w-9 rounded-tr-full rounded-br-full ${
          ["sent_quote"].includes(status) ? "bg-blue-600" : "bg-slate-200"
        }`}
      />
    </div>
  );
}

function SpecialStatus({
  label,
  icon,
  className,
  ...props
}: {
  label: string;
  icon?: ReactNode;
  className: string;
} & HTMLAttributes<HTMLDivElement>) {
  return (
    // special width to match the Progress bar width
    <div
      className={`w-[calc(7.5rem-4px)] flex justify-center gap-2 rounded-4xl border-2 ${className} items-center`}
      {...props}
    >
      {icon}
      <div className="text-xs">{label}</div>
    </div>
  );
}
